import type { PublicProfile } from '@/types'
import ProfileBranding from '@/components/profile/ProfileBranding'

interface MinimalTemplateProps {
  profile: PublicProfile
}

export default function MinimalTemplate({ profile }: MinimalTemplateProps) {
  const { visibility } = profile.profileSettings
  const fullName = `${profile.firstName} ${profile.lastName}`

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-8 md:px-12 py-24 md:py-32">
        {/* Header - Ultra minimal */}
        <header className="mb-24">
          <h1 className="text-6xl md:text-8xl font-light text-gray-900 mb-6 tracking-tight leading-none">
            {fullName}
          </h1>
          {profile.title && (
            <p className="text-2xl md:text-3xl font-light text-gray-500 mb-8">{profile.title}</p>
          )}
          {profile.location && (
            <p className="text-sm uppercase tracking-widest text-gray-400 mb-12">
              {profile.location}
            </p>
          )}
          {profile.bio && (
            <p className="text-lg leading-relaxed text-gray-600 max-w-2xl font-light">
              {profile.bio}
            </p>
          )}
        </header>

        {/* Experience */}
        {visibility.showExperience && profile.experience && profile.experience.length > 0 && (
          <section className="mb-24">
            <h2 className="text-xs uppercase tracking-[0.3em] text-gray-400 mb-12 font-medium">
              Experience
            </h2>
            <div className="space-y-16">
              {profile.experience.map((exp, index) => (
                <div key={index} className="group">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-2xl font-light text-gray-900 mb-1">{exp.jobTitle}</h3>
                      <p className="text-lg text-gray-500 font-light">{exp.companyName}</p>
                    </div>
                    <span className="text-sm text-gray-400 whitespace-nowrap ml-8">
                      {exp.startDate} — {exp.isCurrent ? 'Present' : exp.endDate}
                    </span>
                  </div>
                  {exp.description && (
                    <p className="text-gray-600 leading-relaxed font-light mt-4">
                      {exp.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Education */}
        {visibility.showEducation && profile.education && profile.education.length > 0 && (
          <section className="mb-24">
            <h2 className="text-xs uppercase tracking-[0.3em] text-gray-400 mb-12 font-medium">
              Education
            </h2>
            <div className="space-y-12">
              {profile.education.map((edu, index) => (
                <div key={index}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="text-2xl font-light text-gray-900 mb-1">{edu.degree}</h3>
                      <p className="text-lg text-gray-500 font-light">{edu.institution}</p>
                      {edu.fieldOfStudy && (
                        <p className="text-sm text-gray-400 font-light mt-1">{edu.fieldOfStudy}</p>
                      )}
                    </div>
                    <span className="text-sm text-gray-400 whitespace-nowrap ml-8">
                      {edu.startDate} — {edu.endDate}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Projects */}
        {visibility.showProjects && profile.projects && profile.projects.length > 0 && (
          <section className="mb-24">
            <h2 className="text-xs uppercase tracking-[0.3em] text-gray-400 mb-12 font-medium">
              Projects
            </h2>
            <div className="space-y-16">
              {profile.projects.map((project, index) => (
                <div key={index} className="group">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-2xl font-light text-gray-900">{project.title}</h3>
                        {project.isCurrent && (
                          <span className="text-xs uppercase tracking-widest text-green-600 font-medium">
                            Current
                          </span>
                        )}
                      </div>
                      {project.technologies && (
                        <p className="text-sm text-gray-400 font-light mt-2">
                          {project.technologies}
                        </p>
                      )}
                    </div>
                    {(project.startDate || project.endDate) && (
                      <span className="text-sm text-gray-400 whitespace-nowrap ml-8">
                        {project.startDate || 'N/A'} —{' '}
                        {project.isCurrent ? 'Present' : project.endDate || 'N/A'}
                      </span>
                    )}
                  </div>
                  {project.description && (
                    <p className="text-gray-600 leading-relaxed font-light mt-4">
                      {project.description}
                    </p>
                  )}
                  {(project.projectUrl || project.repositoryUrl) && (
                    <div className="flex gap-8 mt-4">
                      {project.projectUrl && (
                        <a
                          href={project.projectUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm uppercase tracking-widest text-gray-400 hover:text-gray-900 transition-colors border-b border-gray-300 hover:border-gray-900 pb-1"
                        >
                          Live Demo
                        </a>
                      )}
                      {project.repositoryUrl && (
                        <a
                          href={project.repositoryUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm uppercase tracking-widest text-gray-400 hover:text-gray-900 transition-colors border-b border-gray-300 hover:border-gray-900 pb-1"
                        >
                          Source Code
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Skills & Languages - Side by side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-24">
          {/* Skills */}
          {visibility.showSkills && profile.skills && profile.skills.length > 0 && (
            <section>
              <h2 className="text-xs uppercase tracking-[0.3em] text-gray-400 mb-8 font-medium">
                Skills
              </h2>
              <div className="space-y-3">
                {profile.skills.map((skill, index) => (
                  <div key={index} className="text-lg font-light text-gray-700">
                    {skill.skill?.name}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Languages */}
          {visibility.showLanguages && profile.languages && profile.languages.length > 0 && (
            <section>
              <h2 className="text-xs uppercase tracking-[0.3em] text-gray-400 mb-8 font-medium">
                Languages
              </h2>
              <div className="space-y-3">
                {profile.languages?.map((lang, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-lg font-light text-gray-700">
                      {lang.language?.name ?? ''}
                    </span>
                    <span className="text-sm text-gray-400">{lang.proficiencyLevel}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Contact */}
        {visibility.showContact &&
          (profile.email ||
            profile.phone ||
            profile.linkedInUrl ||
            profile.githubUrl ||
            profile.portfolioUrl) && (
            <section className="mb-24 pb-24 border-t border-gray-200 pt-16">
              <h2 className="text-xs uppercase tracking-[0.3em] text-gray-400 mb-8 font-medium">
                Contact
              </h2>
              <div className="space-y-3">
                {profile.email && (
                  <div>
                    <a
                      href={`mailto:${profile.email}`}
                      className="text-lg text-gray-600 hover:text-gray-900 transition-colors font-light"
                    >
                      {profile.email}
                    </a>
                  </div>
                )}
                {profile.phone && (
                  <div>
                    <a
                      href={`tel:${profile.phone}`}
                      className="text-lg text-gray-600 hover:text-gray-900 transition-colors font-light"
                    >
                      {profile.phone}
                    </a>
                  </div>
                )}
                {profile.linkedInUrl && (
                  <div>
                    <a
                      href={profile.linkedInUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg text-gray-600 hover:text-gray-900 transition-colors font-light"
                    >
                      LinkedIn
                    </a>
                  </div>
                )}
                {profile.githubUrl && (
                  <div>
                    <a
                      href={profile.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg text-gray-600 hover:text-gray-900 transition-colors font-light"
                    >
                      GitHub
                    </a>
                  </div>
                )}
                {profile.portfolioUrl && (
                  <div>
                    <a
                      href={profile.portfolioUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg text-gray-600 hover:text-gray-900 transition-colors font-light"
                    >
                      Portfolio
                    </a>
                  </div>
                )}
              </div>
            </section>
          )}

        {/* Documents */}
        {visibility.showDocuments && profile.cvUrl && (
          <section>
            <a
              href={profile.cvUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-sm uppercase tracking-widest text-gray-400 hover:text-gray-900 transition-colors border-b border-gray-300 hover:border-gray-900 pb-1"
            >
              Download CV
            </a>
          </section>
        )}
      </div>

      {/* Subtle Branding */}
      <ProfileBranding variant="light" />
    </div>
  )
}
